import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  AppointmentResponse,
  AppointmentStatus,
} from '@dataTypes/appointment.type'
import moment from 'moment'
import { useAppSelector } from '@store/hooks'
import { getAppointmentsByPatientId } from '@services/api/appointment.api'
import { mockAppointments } from '@assets/mockdata/PatientTray/mockAppointments'
import { getAppointmentLocalMoment } from '@utils/time.util'
import './AppointmentSummary.css'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import PastAndUpcoming from './PastAndUpcoming.component'
// Unscheduled appointments temporarily hidden — restore Unscheduled import + section when ready
// import Unscheduled from './Unscheduled.component'

type AppointmentSummaryProps = {
  patientId: string
  useDemoData?: boolean
  forceEmptyStates?: boolean
}

const AppointmentSummary: React.FC<AppointmentSummaryProps> = ({
  patientId,
  useDemoData = false,
  forceEmptyStates = false,
}) => {
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const [upcomingAppointmentsList, setUpcomingAppointmentsList] = useState<
    AppointmentResponse[]
  >([])
  const [previousAppointmentsList, setPreviousAppointmentsList] = useState<
    AppointmentResponse[]
  >([])
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const isDataReady = useRef(false)

  const applyDemoAppointments = useCallback((): void => {
    const mockAppts = mockAppointments[patientId] || []
    const currentDate = new Date()

    const activeAppts = mockAppts.filter(
      (appt) => appt.appt_status !== AppointmentStatus.Cancelled,
    )
    const previous = activeAppts.filter((appt) =>
      getAppointmentLocalMoment(appt).isBefore(currentDate, 'day'),
    )
    const upcoming = activeAppts.filter((appt) =>
      getAppointmentLocalMoment(appt).isSameOrAfter(currentDate, 'day'),
    )

    setPreviousAppointmentsList(previous)
    setUpcomingAppointmentsList(upcoming)
  }, [patientId])

  const fetchData = useCallback(async (): Promise<void> => {
    showLoading({
      container: '.appointment-summary-page',
    })
    try {
      if (useDemoData) {
        applyDemoAppointments()
        isDataReady.current = true
        hideLoading('.appointment-summary-page')
        return
      }

      const currentDate = new Date()
      const responseUpcoming = await getAppointmentsByPatientId(
        patientId,
        undefined,
        moment(currentDate).format('YYYY-MM-DD'),
      )
      setUpcomingAppointmentsList(responseUpcoming?.data?.appointments || [])
      const responsePrevious = await getAppointmentsByPatientId(
        patientId,
        moment(currentDate).format('YYYY-MM-DD'),
        undefined,
      )
      setPreviousAppointmentsList(responsePrevious?.data?.appointments || [])
      isDataReady.current = true
      hideLoading('.appointment-summary-page')
    } catch (error) {
      hideLoading('.appointment-summary-page')
      console.error(error)
    }
  }, [
    selectedClinic,
    patientId,
    useDemoData,
    applyDemoAppointments,
    showLoading,
    hideLoading,
  ])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return isDataReady.current ? (
    <div className="appointment-summary-container patient-profile-appointments">
      <PastAndUpcoming
        isPast={false}
        appointmentsList={forceEmptyStates ? [] : upcomingAppointmentsList}
        onUpdateData={fetchData}
        isNoData={
          forceEmptyStates ||
          (upcomingAppointmentsList.length === 0 &&
            previousAppointmentsList.length === 0)
        }
      />
      <PastAndUpcoming
        isPast={true}
        appointmentsList={forceEmptyStates ? [] : previousAppointmentsList}
      />
    </div>
  ) : null
}

export default AppointmentSummary
